import type { CommandSourceIo } from '../commandSources/CommandSource.js';
import { ABILITY_DEFS, ARCHETYPE_ABILITY } from '../data/abilities.js';
import { BUILDINGS, MAX_HUBS_PER_PLAYER } from '../data/buildings.js';
import { CAMP_LEASH_RADIUS_PX } from '../data/camps.js';
import { CUT_LINK_TARGET_RANGE_SQ } from '../data/cutLink.js';
import { FORWARD_TURRET_RANGE_SQ } from '../data/forwardTurret.js';
import { JAM_RELAY_TARGET_RANGE_SQ } from '../data/jamRelay.js';
import { SQUAD_DEFS } from '../data/squads.js';
import type { PlayerView, Unsubscribe } from '../matchHost/MatchHost.js';
import { TICK_MS } from '../simulation/State.js';
import { operationalHubsOf } from '../simulation/buildSlots.js';
import { makeAbilityCommand } from '../simulation/commands/AbilityCommand.js';
import { makeAttackMoveCommand } from '../simulation/commands/AttackMoveCommand.js';
import { makeBuildCommand } from '../simulation/commands/BuildCommand.js';
import type { Command } from '../simulation/commands/Command.js';
import { makeMoveCommand } from '../simulation/commands/MoveCommand.js';
import { makePickDoctrineCommand } from '../simulation/commands/PickDoctrineCommand.js';
import { makeProduceSquadCommand } from '../simulation/commands/ProduceSquadCommand.js';
import type { ProductionHub } from '../simulation/entities/Building.js';
import type { Core } from '../simulation/entities/Core.js';
import type { Squad, SquadArchetype } from '../simulation/entities/Squad.js';
import type { EntityId, PlayerId, Tick } from '../simulation/ids.js';
import { otherPlayer, tickOf } from '../simulation/ids.js';

import type { DesiredOrder, RelayBias } from './botShared.js';
import {
  ASSAULT_SKIRMISH_PX,
  ENGAGE_RADIUS_SQ,
  MAP_MIDPOINT,
  RECON_RETREAT_HP_RATIO,
  RETREAT_HP_RATIO,
  SHIELD_HP_TRIGGER_RATIO,
  adaptiveCompositionQuota,
  countOwnBuildings,
  findCoreOf,
  findDisruptableRelay,
  findFortifyableRelay,
  isAtContestedRelay,
  nudgeToward,
  ordersMatch,
  pickFocusFireTarget,
  pickFreeSlot,
  pickKiteTarget,
  pickNextArchetype,
  pickRelayTarget,
  pickRetreatTarget,
  pickScoutTarget,
  sqDist,
  squadValue,
} from './botShared.js';

// Tactical-tier bot for V1. Same CommandSource shape as Recruit (Rule 6) — emits
// only Commands; never reaches into systems or sprites; never reads raw sim state.
//
// Behavior shape (docs/07_BOT_AI.md §Tactical):
//   - Build composition: 1× Engineer early (capture tempo) → 1× Shield (frontline)
//     → 1–2× Assault (DPS). Recon kept as the pre-spawned squad until it dies.
//   - Capture priority weights closer relays + relays we can defend; Recon avoids
//     known turret radii so it doesn't pop into a 1v3.
//   - Push trigger: own army value > enemy army value AND own Core HP > 60%.
//   - Defend trigger: own Core HP < 50% OR enemy squads visible within
//     DEFEND_PROXIMITY_PX of own Core.
//   - Abilities (situational):
//     · Recon Mark Target — paint highest-HP enemy when Assault is in skirmish.
//     · Assault Suppressing Fire — fire when ≥ 2 enemies are in cone.
//     · Engineer Deploy Mini-Turret — at a contested relay or while defending.
//     · Shield Phase Shield — when an ally < 50% HP is within bubble radius.
//   - Build path through Hub (one Hub) + a defensive Turret when economy permits.
//
// Difficulty knob: reaction delay (default 300 ms vs Recruit's 800 ms). Per
// docs/07_BOT_AI.md §Difficulties, reaction delay is the ONLY difficulty
// modulator — no income mod, no perfect-information cheats. V1.7 (3.16)
// tightened the Tactical bar from 400 → 300 ms after the playtest finding
// that the default tier felt too easy; 300 ms keeps a clear gap below
// Commander's 175 ms while removing the "smart-but-omniscient" upper bound
// the old 400 ms left on the table.
//
// Subclassing note: CommanderBotPlayer extends this class to share the snapshot
// pipeline / produce / build / order plumbing; methods that Commander overrides
// are marked `protected` (chooseIntent, maybeEmitDoctrinePick, tryMarkTarget,
// captureOrderFor, the `emit` slot). All other methods stay `private`.

const DEFAULT_REACTION_DELAY_MS = 300;
// AI ticks at 5 Hz: every fourth sim tick. Same cadence as Recruit; squad-level
// decisions don't need 20 Hz.
const AI_TICK_INTERVAL_SIM_TICKS = 4;
const DEFEND_ANCHOR_OFFSET_PX = 90;

// Core HP thresholds (docs/07_BOT_AI.md §Tactical). Defend triggers earlier than
// the 50% defend rule when an enemy is sniffing around the Core.
const DEFEND_HP_THRESHOLD_RATIO = 0.5;
const PUSH_HP_THRESHOLD_RATIO = 0.6;
const DEFEND_PROXIMITY_PX = 600;
const DEFEND_PROXIMITY_SQ = DEFEND_PROXIMITY_PX * DEFEND_PROXIMITY_PX;

// Composition target: walk in order and pick the first archetype below its
// quota. Recon is not produced by default; the pre-spawned Recon is allowed to
// scout until it dies but isn't replaced — UNLESS the adaptive read swaps a
// Recon in (e.g., enemy-heavy Shield matchup where paint matters). squadCap is
// 4 in V0.4 — the quotas sum to that.
//
// V1.5+ overhaul: this is now the *baseline*. The actual quota fed to
// pickNextArchetype is recomputed each AI tick from `adaptiveCompositionQuota`
// against the visible enemy mix — Engineer-first stays the standard opener
// (capture tempo + mini-turret), then we react to what the enemy fields.
const COMPOSITION_ORDER: SquadArchetype[] = ['engineer', 'shield', 'assault', 'recon'];

// Hub economics. We commit to a single Hub once we can afford it — V0.4 caps hubs
// at MAX_HUBS_PER_PLAYER (=2) but Tactical doesn't need a second one.
const TACTICAL_HUB_COUNT = 1;
// Defensive turret: built when we're defending, can afford it, and have a slot.
const TACTICAL_TURRET_COUNT = 1;
// Slot indices we prefer for hub vs. turret. Hub on slot 3 (south side, facing
// the field), Turret on slot 0 (top, screening Core from the front).
const PREFERRED_HUB_SLOT = 3;
const PREFERRED_TURRET_SLOT = 0;

interface InFlightBuild {
  deliveryTick: number;
  slotIndex: number;
}

export interface TacticalBotPlayerOptions {
  playerId: PlayerId;
  reactionDelayMs?: number;
}

export type Intent = 'defend' | 'push' | 'capture';

export class TacticalBotPlayer {
  readonly playerId: PlayerId;
  protected readonly reactionDelayTicks: number;
  private buffer: PlayerView[] = [];
  protected emit: ((cmd: Command) => void) | null = null;
  private unsubSnapshot: Unsubscribe | null = null;
  private aiTickCounter = 0;
  private orderCache = new Map<EntityId, DesiredOrder>();
  // Delivery ticks of produce_squad commands the delayed snapshot hasn't observed
  // yet — used to keep the queue from blowing past squadCap.
  private inFlightProduceDeliveryTicks: number[] = [];
  // Pending hub/turret BuildCommands keyed by what we tried to build, so we don't
  // double-issue while the snapshot is in flight.
  private inFlightHub: InFlightBuild | null = null;
  private inFlightTurret: InFlightBuild | null = null;

  constructor(opts: TacticalBotPlayerOptions) {
    this.playerId = opts.playerId;
    this.reactionDelayTicks = Math.max(
      0,
      Math.round((opts.reactionDelayMs ?? DEFAULT_REACTION_DELAY_MS) / TICK_MS),
    );
  }

  start(io: CommandSourceIo): void {
    this.emit = io.emit;
    this.unsubSnapshot = io.onSnapshot((snap) => this.onSnapshot(snap));
  }

  stop(): void {
    this.unsubSnapshot?.();
    this.unsubSnapshot = null;
    this.emit = null;
    this.buffer = [];
    this.orderCache.clear();
    this.inFlightProduceDeliveryTicks = [];
    this.inFlightHub = null;
    this.inFlightTurret = null;
    this.aiTickCounter = 0;
  }

  // Test seam: deterministic single-step decision against a hand-built snapshot.
  decideForTest(snap: PlayerView): void {
    this.buffer = [snap];
    this.aiTickCounter = AI_TICK_INTERVAL_SIM_TICKS;
    this.onSnapshot(snap);
  }

  private onSnapshot(snap: PlayerView): void {
    this.buffer.push(snap);
    const maxSize = this.reactionDelayTicks + AI_TICK_INTERVAL_SIM_TICKS + 2;
    while (this.buffer.length > maxSize) this.buffer.shift();

    this.aiTickCounter += 1;
    if (this.aiTickCounter < AI_TICK_INTERVAL_SIM_TICKS) return;
    this.aiTickCounter = 0;

    this.decide();
  }

  private decide(): void {
    if (this.emit === null || this.buffer.length === 0) return;

    const liveSnap = this.buffer[this.buffer.length - 1]!;
    if (liveSnap.outcome !== null) return;

    const delayedIdx = Math.max(0, this.buffer.length - 1 - this.reactionDelayTicks);
    const delayed = this.buffer[delayedIdx]!;

    this.inFlightProduceDeliveryTicks = this.inFlightProduceDeliveryTicks.filter(
      (deliveryTick) => deliveryTick > delayed.tick,
    );
    if (this.inFlightHub !== null && this.inFlightHub.deliveryTick <= delayed.tick) {
      this.inFlightHub = null;
    }
    if (this.inFlightTurret !== null && this.inFlightTurret.deliveryTick <= delayed.tick) {
      this.inFlightTurret = null;
    }
    for (const id of [...this.orderCache.keys()]) {
      if (delayed.squads[id] === undefined) this.orderCache.delete(id);
    }

    const ownSquads: Squad[] = [];
    const enemySquads: Squad[] = [];
    for (const sq of Object.values(delayed.squads)) {
      if (sq.ownerId === this.playerId) ownSquads.push(sq);
      else enemySquads.push(sq);
    }
    const ownCore = findCoreOf(delayed, this.playerId);
    const enemyCore = findCoreOf(delayed, otherPlayer(this.playerId));
    const intent = this.chooseIntent(delayed, ownSquads, enemySquads, ownCore);

    // Ability decisions first so that the squad's ability state going into the
    // next tick reflects this AI tick's reads. (Ordering within the same tick
    // doesn't affect the sim, but it keeps the test seam predictable.)
    this.maybeEmitAbilities(delayed, ownSquads, enemySquads, liveSnap.tick);

    for (const sq of ownSquads) {
      const desired = this.orderForSquad(intent, sq, delayed, ownCore, enemyCore, ownSquads, enemySquads);
      if (desired !== null) this.emitOrderIfNew(sq, desired, liveSnap.tick);
    }

    this.maybeEmitProduce(delayed, ownSquads, enemySquads, liveSnap.tick);
    this.maybeEmitBuild(delayed, intent, liveSnap.tick);
    this.maybeEmitDoctrinePick(delayed, liveSnap.tick);
  }

  // Tactical doctrine pick: 'bulwark' if we already have a Hub or Turret of our
  // own (so the +25% range / +10% build speed compounds with what we've built);
  // otherwise 'vanguard' as a safe sidegrade. Deterministic (Rule 8). Routed
  // through the same PickDoctrineCommand a human would emit (Rule 6).
  protected maybeEmitDoctrinePick(snap: PlayerView, liveTick: Tick): void {
    if (this.emit === null) return;
    if (!snap.doctrinePickWindowOpen) return;
    if (snap.players[this.playerId].doctrineId !== null) return;
    let hasStructure = false;
    for (const b of Object.values(snap.buildings)) {
      if (b.ownerId !== this.playerId) continue;
      if (b.kind === 'production_hub' || b.kind === 'turret') {
        hasStructure = true;
        break;
      }
    }
    const pick = hasStructure ? 'bulwark' : 'vanguard';
    this.emit(
      makePickDoctrineCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        doctrineId: pick,
      }),
    );
  }

  // _snap is unused here but receives the full delayed snapshot; Commander's
  // override consults the enemy's production queue + surge state, so the
  // signature must be stable across the hierarchy.
  protected chooseIntent(
    _snap: PlayerView,
    ownSquads: Squad[],
    enemySquads: Squad[],
    ownCore: Core | null,
  ): Intent {
    if (ownCore !== null && ownCore.maxHp > 0) {
      if (ownCore.hp / ownCore.maxHp < DEFEND_HP_THRESHOLD_RATIO) return 'defend';
      for (const enemy of enemySquads) {
        if (sqDist(enemy.pos, ownCore.pos) <= DEFEND_PROXIMITY_SQ) return 'defend';
      }
    }
    const ownValue = ownSquads.reduce((s, sq) => s + squadValue(sq), 0);
    const enemyValue = enemySquads.reduce((s, sq) => s + squadValue(sq), 0);
    const coreOk = ownCore === null
      ? true
      : ownCore.maxHp <= 0
        ? false
        : ownCore.hp / ownCore.maxHp > PUSH_HP_THRESHOLD_RATIO;
    if (ownValue > enemyValue && coreOk && ownSquads.length >= 2) return 'push';
    return 'capture';
  }

  private orderForSquad(
    intent: Intent,
    squad: Squad,
    snap: PlayerView,
    ownCore: Core | null,
    enemyCore: Core | null,
    ownSquads: Squad[],
    enemySquads: Squad[],
  ): DesiredOrder | null {
    // Combat micro layer — applied to every intent. Order of precedence:
    //   1. Channeling: don't issue anything (sim's MovementSystem ignores it
    //      anyway, but we save the order-cache churn).
    //   2. Wounded retreat: if HP fraction drops below the per-archetype
    //      threshold AND a meaningful retreat anchor exists, pull back. The
    //      anchor is a healthy Shield first (so Phase Shield can bank), Core
    //      as fallback. Recon retreats earlier (45% vs 30%) because Recon is
    //      paper and a dead Recon is worth more alive than dying for one
    //      shot of damage.
    //   3. Recon kite: in capture/push intents, a Recon under enemy pressure
    //      backs straight away from the closest enemy while still firing.
    //      Skipped under defend (we're already pulling home).
    //   4. Focus-fire redirect: when an enemy squad is inside ENGAGE_RADIUS_SQ,
    //      orient the attack-move toward that enemy's *position* so
    //      CombatSystem's nearest-target acquisition picks it (we can't
    //      override target selection via commands — Rule 6 — so we move the
    //      squad to make the desired enemy the natural nearest).
    if (squad.channelingAbility !== null) return null;

    const microRetreat = this.maybeRetreatOrder(squad, ownSquads, ownCore, enemySquads);
    if (microRetreat !== null) return microRetreat;

    if (intent !== 'defend' && squad.archetype === 'recon') {
      const kite = this.maybeKiteOrder(squad, enemySquads);
      if (kite !== null) return kite;
    }

    if (intent === 'defend') {
      if (ownCore === null) return null;
      // Defend with awareness: if an enemy is already inside our ring, attack
      // them directly so we don't ball up on the Core sprite. Otherwise hold
      // the canonical anchor.
      const focusTarget = pickFocusFireTarget(squad, enemySquads, DEFEND_PROXIMITY_SQ);
      if (focusTarget !== null) {
        return { kind: 'attack_move', target: { x: focusTarget.pos.x, y: focusTarget.pos.y } };
      }
      const anchor = nudgeToward(
        { x: ownCore.pos.x, y: ownCore.pos.y },
        MAP_MIDPOINT,
        DEFEND_ANCHOR_OFFSET_PX,
      );
      return { kind: 'attack_move', target: anchor };
    }

    if (intent === 'push' && enemyCore !== null) {
      // Focus-fire layer over push: redirect at a nearby enemy first so the
      // push doesn't ignore a vulnerable squad on the way to the Core. The
      // Core only becomes the canonical fallback when no engagement target
      // is within ENGAGE_RADIUS.
      const focusTarget = pickFocusFireTarget(squad, enemySquads);
      if (focusTarget !== null) {
        return { kind: 'attack_move', target: { x: focusTarget.pos.x, y: focusTarget.pos.y } };
      }
      return { kind: 'attack_move', target: { x: enemyCore.pos.x, y: enemyCore.pos.y } };
    }

    // Capture path — focus-fire takes precedence over relay routing when an
    // enemy is sitting on our intended path (the cost of attack-moving at the
    // enemy's position is one diversion; the cost of walking past an enemy is
    // a free pot-shot).
    const focusTarget = pickFocusFireTarget(squad, enemySquads);
    if (focusTarget !== null && squad.archetype !== 'recon' && squad.archetype !== 'engineer') {
      return { kind: 'attack_move', target: { x: focusTarget.pos.x, y: focusTarget.pos.y } };
    }
    return this.captureOrderFor(squad, snap, ownCore, enemyCore, enemySquads);
  }

  // Wounded retreat hook. Returns a retreat order when the squad is below the
  // HP fraction for its archetype AND a meaningful pullback anchor exists.
  // Skipped when the squad is currently sitting on a contested relay (we'd
  // rather hold the cap than abandon it), or when no enemy is in firing range
  // (no immediate threat — let it heal up in place or continue its task).
  // Protected so Commander can tighten the threshold via retreatThresholdFor.
  protected maybeRetreatOrder(
    squad: Squad,
    ownSquads: Squad[],
    ownCore: Core | null,
    enemySquads: Squad[],
  ): DesiredOrder | null {
    if (squad.maxHp <= 0) return null;
    const ratio = squad.hp / squad.maxHp;
    const threshold = this.retreatThresholdFor(squad);
    if (ratio >= threshold) return null;
    // No threat in range → no need to retreat (let healing / continuation
    // happen). Reusing ENGAGE_RADIUS keeps the gate symmetric with focus-fire.
    let anyEnemyClose = false;
    for (const e of enemySquads) {
      if (e.hp <= 0) continue;
      if (sqDist(e.pos, squad.pos) <= ENGAGE_RADIUS_SQ) {
        anyEnemyClose = true;
        break;
      }
    }
    if (!anyEnemyClose) return null;
    const retreat = pickRetreatTarget(squad, ownSquads, ownCore);
    if (retreat === null) return null;
    return { kind: 'move', target: retreat };
  }

  // Per-archetype retreat threshold. Tactical uses RECON_RETREAT_HP_RATIO for
  // Recon, RETREAT_HP_RATIO for everything else. Commander overrides this hook
  // to retreat earlier (its macro game compounds, so a saved squad is more
  // valuable than a dying-trade exchange).
  protected retreatThresholdFor(squad: Squad): number {
    return squad.archetype === 'recon' ? RECON_RETREAT_HP_RATIO : RETREAT_HP_RATIO;
  }

  // Recon kite hook. Returns a kite-back order when an enemy is closing inside
  // Recon's firing band; null when no kite is warranted. Protected so
  // Commander can override (e.g., Commander Recon also kites away from
  // operational mini-turrets).
  protected maybeKiteOrder(squad: Squad, enemySquads: Squad[]): DesiredOrder | null {
    const def = SQUAD_DEFS[squad.archetype];
    const kite = pickKiteTarget(squad, enemySquads, def.range);
    if (kite === null) return null;
    return { kind: 'move', target: kite };
  }

  // Capture-mode routing. Each archetype gets a distinct goal: Recon avoids
  // turret radii, Engineer rushes the closest non-owned relay (50% capture
  // bonus pays for the trip), Assault and Shield converge on whichever relay
  // we already lead so they form a frontline. Commander overrides the bias
  // argument when Underdog Surge tilts the strategic read.
  //
  // V1.5+ overhaul: Recon in the early game prefers a *scout* target — an
  // unowned, unguarded relay — over the standard pickRelayTarget output.
  // This wires the "Idle scout" pipeline step documented in 07_BOT_AI.md §6
  // that was previously a no-op: the most interesting non-combat unit now
  // actually expands map vision. Once all relays are owned (mid-late game),
  // the Recon falls through to the standard router.
  protected captureOrderFor(
    squad: Squad,
    snap: PlayerView,
    ownCore: Core | null,
    enemyCore: Core | null,
    enemySquads: Squad[],
  ): DesiredOrder | null {
    if (squad.archetype === 'recon') {
      const scoutTarget = pickScoutTarget(snap, squad, this.playerId);
      if (scoutTarget !== null) {
        return { kind: 'move', target: scoutTarget };
      }
    }
    const target = pickRelayTarget(
      snap,
      squad,
      this.playerId,
      ownCore,
      enemySquads,
      this.relayBias(snap),
    );
    if (target !== null) {
      // V1.5 neutral-camp routing: if the picked relay is guarded, walking
      // straight to the centre puts the squad inside the 220 px camp aggro
      // ring with patrols at 110 px — the squad dies before capturing. Send
      // it at the nearest patrolling guard instead so it engages the camp
      // from firing range; once the camp is cleared (guardedBy → null) the
      // next bot tick routes it to the now-safe relay centre. Recon is
      // excluded — it's paste in camp range and pickRelayTarget already
      // dumps a 6M penalty on guarded relays for Recon, so the only way a
      // Recon lands here is "all relays guarded", in which case it's better
      // off sneaking the original path than picking a fight.
      if (squad.archetype !== 'recon') {
        const guarded = findGuardedNodeAt(snap, target);
        if (guarded !== null) {
          const guard = nearestGuardOfCampAt(snap, squad.pos, guarded.pos);
          if (guard !== null) {
            return { kind: 'attack_move', target: { x: guard.x, y: guard.y } };
          }
        }
      }
      return { kind: squad.archetype === 'recon' ? 'move' : 'attack_move', target };
    }
    if (enemyCore !== null) {
      return { kind: 'attack_move', target: { x: enemyCore.pos.x, y: enemyCore.pos.y } };
    }
    return null;
  }

  // Tactical never tilts the relay bias on its own; Commander overrides this
  // hook to prefer 'capture' under own surge and 'pressure_leader' under
  // enemy surge.
  //
  // V1.5+ Slice 8 — Signal Storm Endgame: when the storm phase is active,
  // every bot squad pivots to 'pressure_leader' regardless of subclass. The
  // storm is a "decisions are now mandatory" phase — the bot mirrors the
  // human heuristic of pushing the leader's relays because dominance is the
  // timer-decided win condition. Commander's override calls super first so
  // the storm gate composes with surge state (storm is the OUTERMOST gate;
  // see CommanderBotPlayer.relayBias).
  protected relayBias(snap: PlayerView): RelayBias {
    if (snap.phase === 'signal_storm') return 'pressure_leader';
    return 'default';
  }

  private emitOrderIfNew(squad: Squad, desired: DesiredOrder, liveTick: Tick): void {
    if (this.emit === null) return;
    const cached = this.orderCache.get(squad.id);
    if (cached !== undefined && ordersMatch(cached, desired)) return;
    const cmdTick = tickOf(liveTick + 1);
    if (desired.kind === 'move') {
      this.emit(
        makeMoveCommand({
          issuedBy: this.playerId,
          tick: cmdTick,
          squadIds: [squad.id],
          target: desired.target,
        }),
      );
    } else {
      this.emit(
        makeAttackMoveCommand({
          issuedBy: this.playerId,
          tick: cmdTick,
          squadIds: [squad.id],
          target: desired.target,
        }),
      );
    }
    this.orderCache.set(squad.id, { kind: desired.kind, target: { ...desired.target } });
  }

  // Composition-aware producer. Picks the next archetype from COMPOSITION_PRIORITY
  // that we don't already have (alive or queued); falls back to Assault. Routes
  // through whichever of {Core, operational Hubs} has the shortest queue so the
  // bot uses all of its parallel production capacity (V1.7 Hubs ADD a queue
  // rather than replacing the Core, so a bot with 1 Hub has 2 queues and 2
  // Hubs gives 3 queues — see BuildingSystem header).
  //
  // V1.5+ overhaul: the quota is now adaptive against the visible enemy mix
  // (see `adaptiveCompositionQuota`). The bot maintains the same Engineer-first
  // priority for tempo, but the *quota* it walks against changes per matchup —
  // an Assault-heavy enemy bumps the Shield quota; a Shield-heavy enemy bumps
  // the Recon quota so the bot has paint capacity to chip the Shield front.
  // Commander overrides the quota-build read further (composeQuotaFor hook).
  private maybeEmitProduce(
    snap: PlayerView,
    ownSquads: Squad[],
    enemySquads: Squad[],
    liveTick: Tick,
  ): void {
    if (this.emit === null) return;
    const producer = this.findProducer(snap);
    if (producer === null) return;
    const player = snap.players[this.playerId];
    const queueLength = producer.slot.queue.length;
    const inFlight = this.inFlightProduceDeliveryTicks.length;
    const projected = player.squadCount + queueLength + inFlight;
    if (projected >= player.squadCap) return;

    const quota = this.composeQuotaFor(snap, enemySquads, player.squadCap);
    const archetype = pickNextArchetype(
      ownSquads,
      producer.slot.queue,
      player.signal,
      COMPOSITION_ORDER,
      quota,
    );
    if (archetype === null) return;
    const def = SQUAD_DEFS[archetype];
    if (player.signal < def.cost) return;

    this.emit(
      makeProduceSquadCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        producerId: producer.id,
        archetype,
      }),
    );
    this.inFlightProduceDeliveryTicks.push(liveTick + 1);
  }

  // Composition quota selection hook. Tactical reads the enemy mix and lets
  // `adaptiveCompositionQuota` choose between the canonical 1-1-2 baseline and
  // a counter-skew. Commander overrides this to add matchup-aware refinements
  // (e.g., over-build Shield under enemy push pressure). `_snap` is here so
  // Commander's override can consult fog / link state without changing the
  // signature.
  protected composeQuotaFor(
    _snap: PlayerView,
    enemySquads: Squad[],
    squadCap: number,
  ): Record<SquadArchetype, number> {
    return adaptiveCompositionQuota(enemySquads, squadCap);
  }

  // Resolve who's producing for us right now: whichever of {Core, operational
  // Hubs} has the shortest queue, so the bot fills all parallel queues evenly
  // instead of stacking everything on the Hub. Ties resolve to the Core so
  // pre-Hub behaviour is preserved bit-for-bit.
  private findProducer(
    snap: PlayerView,
  ): { id: EntityId; slot: { queue: SquadArchetype[]; progress: number } } | null {
    const candidates: Array<{ id: EntityId; slot: { queue: SquadArchetype[]; progress: number } }> = [];
    const core = findCoreOf(snap, this.playerId);
    if (core !== null && core.produces !== null) {
      candidates.push({ id: core.id, slot: core.produces });
    }
    for (const hub of operationalHubsOf(snap, this.playerId)) {
      const h = hub as ProductionHub;
      candidates.push({ id: h.id, slot: h.produces });
    }
    if (candidates.length === 0) return null;
    let best = candidates[0]!;
    for (let i = 1; i < candidates.length; i++) {
      if (candidates[i]!.slot.queue.length < best.slot.queue.length) {
        best = candidates[i]!;
      }
    }
    return best;
  }

  // Hub + defensive Turret. We never own more than the canonical counts; in-flight
  // tracking prevents double-issue while the BuildCommand is still pending.
  private maybeEmitBuild(snap: PlayerView, intent: Intent, liveTick: Tick): void {
    if (this.emit === null) return;
    const core = findCoreOf(snap, this.playerId);
    if (core === null) return;
    const player = snap.players[this.playerId];

    const hubs = countOwnBuildings(snap, this.playerId, 'production_hub');
    if (
      hubs < Math.min(TACTICAL_HUB_COUNT, MAX_HUBS_PER_PLAYER)
      && this.inFlightHub === null
      && player.signal >= BUILDINGS.production_hub.cost
    ) {
      const slot = pickFreeSlot(snap, core, PREFERRED_HUB_SLOT);
      if (slot !== null) {
        this.emit(
          makeBuildCommand({
            issuedBy: this.playerId,
            tick: tickOf(liveTick + 1),
            coreId: core.id,
            slotIndex: slot,
            building: 'production_hub',
          }),
        );
        this.inFlightHub = { deliveryTick: liveTick + 1, slotIndex: slot };
        return;
      }
    }

    if (intent !== 'defend') return;
    const turrets = countOwnBuildings(snap, this.playerId, 'turret');
    if (turrets >= TACTICAL_TURRET_COUNT) return;
    if (this.inFlightTurret !== null) return;
    if (player.signal < BUILDINGS.turret.cost) return;
    const slot = pickFreeSlot(snap, core, PREFERRED_TURRET_SLOT);
    if (slot === null) return;
    this.emit(
      makeBuildCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        coreId: core.id,
        slotIndex: slot,
        building: 'turret',
      }),
    );
    this.inFlightTurret = { deliveryTick: liveTick + 1, slotIndex: slot };
  }

  private maybeEmitAbilities(
    snap: PlayerView,
    ownSquads: Squad[],
    enemySquads: Squad[],
    liveTick: Tick,
  ): void {
    if (this.emit === null) return;
    for (const sq of ownSquads) {
      // V1.5+ Slice 2 — a channeling Recon can't fire any ability (sim
      // re-checks but the bot shouldn't even try). Skip the whole switch so
      // we don't burn an AI tick on it.
      if (sq.channelingAbility !== null) continue;
      // Primary (Q) gated on `abilityCooldown` — same V1 semantics.
      if (sq.abilityCooldown <= 0) {
        switch (sq.archetype) {
          case 'recon':
            this.tryMarkTarget(sq, ownSquads, enemySquads, liveTick);
            break;
          case 'assault':
            this.trySuppressingFire(sq, enemySquads, liveTick);
            break;
          case 'engineer':
            this.tryDeployMiniTurret(sq, snap, enemySquads, liveTick);
            break;
          case 'shield':
            this.tryPhaseShield(sq, ownSquads, liveTick);
            break;
        }
      }
      // V1.5+ Slice 2 / Slice 5 / Slice 6 / Slice 7 — secondary (E).
      // Independent of `abilityCooldown` and dispatched AFTER the primary
      // so the secondary doesn't lock out the primary in the same AI tick.
      // Cut Link, Jam Relay, Disrupt Uplink, and Fortify Relay all arm
      // multi-second channels, so once one fires the squad will skip the
      // next few AI ticks via the channelingAbility guard.
      if (sq.secondaryAbilityCooldown <= 0) {
        if (sq.archetype === 'recon') {
          this.tryCutLink(sq, snap, liveTick);
        } else if (sq.archetype === 'engineer') {
          this.tryJamRelay(sq, snap, liveTick);
        } else if (sq.archetype === 'assault') {
          this.tryDisruptUplink(sq, snap, liveTick);
        } else if (sq.archetype === 'shield') {
          this.tryFortifyRelay(sq, snap, liveTick);
        }
      }
    }
  }

  // V1.5+ Slice 2 — Cut Link decision. Tactical fires Cut Link on the most
  // valuable in-range relay: an enemy-owned, currently-connected relay (cuts
  // matter most when they break a live chain). Falls back to neutral relays
  // only when no enemy chain link is in range. Owned relays are never cut by
  // the bot (no strategic value). Deterministic — no RNG (Rule 8).
  //
  // V1.5+ overhaul fix: the original scoring had `bestScore = Math.max(...)`
  // BEFORE the comparison, which made `score >= bestScore` trivially true
  // and the last iterated node always won regardless of actual score. The
  // strict-greater check below now actually picks the best-scoring node.
  // V1.5+ overhaul addition: the score now also factors in the number of
  // enemy forward `mini_turret`s anchored on the relay — cutting a relay
  // that anchors three turrets is dramatically more valuable than cutting
  // a back-line relay with no forward presence (§15 dynamic).
  protected tryCutLink(recon: Squad, snap: PlayerView, liveTick: Tick): void {
    if (this.emit === null) return;
    const enemyId: PlayerId = otherPlayer(this.playerId);
    // Pre-collect enemy mini-turret positions so we can ask "how much
    // forward firepower does this relay anchor?" in O(turrets) per relay.
    const enemyTurrets: { x: number; y: number }[] = [];
    for (const b of Object.values(snap.buildings)) {
      if (b.kind !== 'mini_turret') continue;
      if (b.ownerId !== enemyId) continue;
      if (!b.isOnline) continue;
      enemyTurrets.push(b.pos);
    }
    let bestNodeId: EntityId | null = null;
    let bestScore = -Infinity;
    for (const node of Object.values(snap.controlNodes)) {
      if (node.ownerId === this.playerId) continue;
      if (sqDist(recon.pos, node.pos) > CUT_LINK_TARGET_RANGE_SQ) continue;
      // Skip nodes already in a cut window — the cut would be a no-op on a
      // node that's already severed.
      if (snap.tick < node.linkCutUntilTick) continue;
      // Score: enemy-connected relay > enemy-isolated > neutral. Within a
      // tier, every anchored online turret adds a big bonus — cutting the
      // chain dims them all simultaneously.
      let score = 0;
      if (node.ownerId === enemyId) score += node.isConnected ? 1000 : 500;
      else score += 100; // neutral
      for (const t of enemyTurrets) {
        if (sqDist(node.pos, t) <= FORWARD_TURRET_RANGE_SQ) score += 800;
      }
      if (score > bestScore) {
        bestScore = score;
        bestNodeId = node.id;
      }
    }
    if (bestNodeId === null) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        squadId: recon.id,
        hotkey: 'E',
        target: { kind: 'control_node', id: bestNodeId },
      }),
    );
  }

  // V1.5+ Slice 5 — Jam Relay decision. Mirrors `tryCutLink`'s scoring shape
  // but skips on `jammedUntilTick` instead of `linkCutUntilTick`. Targets
  // enemy-connected relays first (jamming a relay that's already isolated
  // is wasted vision-denial — the owner already can't see from it). Owned
  // relays are never jammed (no strategic value); neutral relays are skipped
  // (no defender to deny vision from). Deterministic — no RNG (Rule 8).
  //
  // V1.5+ overhaul fix: same `bestScore = Math.max(...)`-before-compare bug
  // as tryCutLink — the original picked whichever in-range enemy-connected
  // node was iterated *last*, not the best-scoring. Now uses strict-greater.
  protected tryJamRelay(engineer: Squad, snap: PlayerView, liveTick: Tick): void {
    if (this.emit === null) return;
    const enemyId: PlayerId = otherPlayer(this.playerId);
    let bestNodeId: EntityId | null = null;
    let bestScore = -Infinity;
    for (const node of Object.values(snap.controlNodes)) {
      // Jam targets enemy relays only — vision denial against your own owner
      // is a no-op, and neutral relays have no defender to deny vision from.
      if (node.ownerId !== enemyId) continue;
      if (sqDist(engineer.pos, node.pos) > JAM_RELAY_TARGET_RANGE_SQ) continue;
      // Skip nodes already jammed — the jam would just refresh the window,
      // which is fine but lower-value than landing one elsewhere if the bot
      // controls multiple Engineers.
      if (snap.tick < node.jammedUntilTick) continue;
      // Score: enemy-connected relay (vision-denial actually denies live
      // vision) > enemy-isolated (already no vision; jam still blocks the
      // next reconnect's vision pulse for the jam window, but it's marginal).
      // Tie-break by closer-to-engineer: a closer jam target costs the
      // Engineer less commute and reduces interrupt exposure.
      let score = node.isConnected ? 1000 : 100;
      score -= Math.sqrt(sqDist(engineer.pos, node.pos)); // closer = higher
      if (score > bestScore) {
        bestScore = score;
        bestNodeId = node.id;
      }
    }
    if (bestNodeId === null) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        squadId: engineer.id,
        hotkey: 'E',
        target: { kind: 'control_node', id: bestNodeId },
      }),
    );
  }

  // V1.5+ Slice 6 — Disrupt Uplink decision. Tactical fires Disrupt Uplink
  // only when an enemy forward `mini_turret` is anchored on a non-own relay
  // within DISRUPT_UPLINK_TARGET_RANGE_PX. Per Rule 18 — disruption MUST be
  // felt on the battlefield; an Assault committing the channel against a
  // relay with no anchored turret would just spend cooldown for zero
  // observable effect. `findDisruptableRelay` enforces this gate; if it
  // returns null, the bot doesn't fire.
  //
  // The bot does NOT discriminate by enemy chain state (cut / jammed) — a
  // disrupt landing on a relay where the upstream chain is currently broken
  // still pays off when the chain reconnects (the disruption window is
  // 25 s; many cuts expire inside that window).
  protected tryDisruptUplink(assault: Squad, snap: PlayerView, liveTick: Tick): void {
    if (this.emit === null) return;
    const target = findDisruptableRelay(snap, assault, this.playerId);
    if (target === null) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        squadId: assault.id,
        hotkey: 'E',
        target: { kind: 'control_node', id: target.id },
      }),
    );
  }

  // V1.5+ Slice 7 — Fortify Relay decision. Tactical fires Fortify Relay
  // only when `findFortifyableRelay` returns a candidate (own relay, own
  // forward turret anchored, threat present). The threat gate is
  // §17-critical: fortifying safe rear relays would tank the relay-flip
  // rate by making turrets harder to crack. The helper enforces this so
  // the bot's tactical commit always lands on a relay where the buff
  // matters AND the immunity could deflect an incoming disruption.
  protected tryFortifyRelay(shield: Squad, snap: PlayerView, liveTick: Tick): void {
    if (this.emit === null) return;
    const target = findFortifyableRelay(snap, shield, this.playerId);
    if (target === null) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        squadId: shield.id,
        hotkey: 'E',
        target: { kind: 'control_node', id: target.id },
      }),
    );
  }

  protected tryMarkTarget(
    recon: Squad,
    ownSquads: Squad[],
    enemySquads: Squad[],
    liveTick: Tick,
  ): void {
    if (this.emit === null) return;
    const def = ABILITY_DEFS[ARCHETYPE_ABILITY.recon];
    const range = def.markRange ?? 600;
    const rangeSq = range * range;
    let bestTarget: Squad | null = null;
    let bestHp = -1;
    for (const enemy of enemySquads) {
      if (sqDist(enemy.pos, recon.pos) > rangeSq) continue;
      if (enemy.hp > bestHp) {
        bestHp = enemy.hp;
        bestTarget = enemy;
      }
    }
    if (bestTarget === null) return;
    // Only paint when an Assault ally is close enough to bank the +10% damage.
    let assaultClose = false;
    const skirmishSq = ASSAULT_SKIRMISH_PX * ASSAULT_SKIRMISH_PX;
    for (const ally of ownSquads) {
      if (ally.archetype !== 'assault') continue;
      if (sqDist(ally.pos, bestTarget.pos) <= skirmishSq) {
        assaultClose = true;
        break;
      }
    }
    if (!assaultClose) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        squadId: recon.id,
        target: { kind: 'squad', id: bestTarget.id },
      }),
    );
  }

  // V1.5+ overhaul: value-aware Suppressing Fire trigger. The original
  // version fired whenever ≥2 enemies were in the cone, which burned the
  // 30 s cooldown on a fleeing Recon pair or two near-dead squads. The
  // new gate sums squad COST (not count) inside the cone — Suppressing
  // Fire is now spent on a real threat (Shield-anchored frontline, or two
  // healthy Assault) rather than any two warm bodies. Threshold of 240
  // ≈ 2× Assault cost — calibrated to "Suppressing Fire saves a real
  // engagement, not a chase."
  private trySuppressingFire(assault: Squad, enemySquads: Squad[], liveTick: Tick): void {
    if (this.emit === null) return;
    const def = ABILITY_DEFS[ARCHETYPE_ABILITY.assault];
    const range = def.suppressionRange ?? 320;
    const halfAngle = def.suppressionHalfAngleRad ?? Math.PI / 4;
    const rangeSq = range * range;
    let coneValue = 0;
    let coneCount = 0;
    for (const enemy of enemySquads) {
      const dx = enemy.pos.x - assault.pos.x;
      const dy = enemy.pos.y - assault.pos.y;
      const dSq = dx * dx + dy * dy;
      if (dSq > rangeSq) continue;
      if (dSq > 0) {
        const targetAngle = Math.atan2(dy, dx);
        let diff = targetAngle - assault.facing;
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;
        if (Math.abs(diff) > halfAngle) continue;
      }
      coneCount += 1;
      coneValue += squadValue(enemy);
    }
    // Two gates: the original ≥2 count rule (so we still fire on swarms) AND
    // a value floor (so we don't burn the cooldown on two near-dead Recons).
    // OR an even simpler "one big tank" exception: a single Shield in cone
    // is worth suppressing because the Shield's slow speed compounds with
    // the -30% suppression debuff to neutralise the frontline.
    const SUPPRESS_VALUE_FLOOR = 240;
    const valueGate = coneValue >= SUPPRESS_VALUE_FLOOR;
    const swarmGate = coneCount >= 2 && coneValue >= 120;
    if (!valueGate && !swarmGate) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        squadId: assault.id,
      }),
    );
  }

  private tryDeployMiniTurret(
    engineer: Squad,
    snap: PlayerView,
    enemySquads: Squad[],
    liveTick: Tick,
  ): void {
    if (this.emit === null) return;
    const def = ABILITY_DEFS[ARCHETYPE_ABILITY.engineer];
    const detectRange = def.miniTurretRange ?? 320;
    const detectSq = detectRange * detectRange;
    // Drop a mini-turret if (a) we're at a contested relay or (b) any enemy
    // squad is in mini-turret range. Both situations make the 30 s timed turret
    // worth its cooldown.
    const atContested = isAtContestedRelay(snap, engineer);
    let enemyInRange = false;
    if (!atContested) {
      for (const enemy of enemySquads) {
        if (sqDist(enemy.pos, engineer.pos) <= detectSq) {
          enemyInRange = true;
          break;
        }
      }
    }
    if (!atContested && !enemyInRange) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        squadId: engineer.id,
      }),
    );
  }

  private tryPhaseShield(shield: Squad, ownSquads: Squad[], liveTick: Tick): void {
    if (this.emit === null) return;
    const def = ABILITY_DEFS[ARCHETYPE_ABILITY.shield];
    const radius = def.shieldRadius ?? 140;
    const radiusSq = radius * radius;
    let lowAllyInBubble = false;
    for (const ally of ownSquads) {
      if (ally.id === shield.id) continue;
      if (ally.maxHp <= 0) continue;
      if (ally.hp / ally.maxHp >= SHIELD_HP_TRIGGER_RATIO) continue;
      if (sqDist(ally.pos, shield.pos) <= radiusSq) {
        lowAllyInBubble = true;
        break;
      }
    }
    if (!lowAllyInBubble) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        squadId: shield.id,
      }),
    );
  }
}

// Returns the ControlNode whose position equals the picked relay target AND
// which is currently guarded by a neutral camp. Position equality is exact —
// pickRelayTarget echoes node.pos verbatim, and node positions are immutable
// after world build. Null when the picked relay is unguarded (or no match,
// which can happen for a one-tick race after the flag flips).
function findGuardedNodeAt(
  snap: PlayerView,
  pos: { x: number; y: number },
): { pos: { x: number; y: number } } | null {
  for (const node of Object.values(snap.controlNodes)) {
    if (node.guardedBy !== 'neutral_camp') continue;
    if (node.pos.x === pos.x && node.pos.y === pos.y) return node;
  }
  return null;
}

// Among neutral units within CAMP_LEASH_RADIUS_PX of `relayPos` (i.e., guards
// belonging to THIS camp, ignoring any other camps elsewhere on the map),
// pick the one closest to `squadPos`. That gives the squad the most
// accessible flank of the camp to attack-move into.
function nearestGuardOfCampAt(
  snap: PlayerView,
  squadPos: { x: number; y: number },
  relayPos: { x: number; y: number },
): { x: number; y: number } | null {
  const leashSq = CAMP_LEASH_RADIUS_PX * CAMP_LEASH_RADIUS_PX;
  let best: { x: number; y: number } | null = null;
  let bestSq = Infinity;
  for (const u of Object.values(snap.neutralUnits)) {
    if (sqDist(u.pos, relayPos) > leashSq) continue;
    const d = sqDist(u.pos, squadPos);
    if (d < bestSq) {
      bestSq = d;
      best = { x: u.pos.x, y: u.pos.y };
    }
  }
  return best;
}
